import React, { useEffect, useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { APP_SETTING_DEFAULTS } from '../services/appSettings';
import { THEME_PRESETS, DEFAULT_THEME_ID, applyTheme } from '../services/theme';
import { AppToggleSettings as AppSettingsType } from '../types';
import './AppSettings.css';

// ---------------------------------------------------------------------------
// Structural reconstruction of app_setting_layout.xml (+ app_setting_customer
// and app_setting_product includes). The Android screen is one long scroll of
// collapsible, orange-headed sections full of SwitchCompat toggles, a couple of
// RadioGroups, and a day-end-time Spinner, ending in a dark-orange "Advance
// Settings" group. We render it as a clean two-column grid of collapsible
// cards. Toggle `key`s match the Android `switch_*` ids (prefix dropped).
// ---------------------------------------------------------------------------

type Row =
  | { kind: 'swatch'; key: string; label: string }
  | { kind: 'toggle'; key: string; label: string }
  | { kind: 'radio'; key: string; label: string; options: string[]; defaultValue: string }
  | { kind: 'spinner'; key: string; label: string; options: string[]; defaultValue: string }
  | { kind: 'note'; text: string }
  | { kind: 'subheader'; text: string };

interface Section {
  id: string;
  title: string;
  rows: Row[];
}

// Top group — the always-expanded billing toggles + the primary collapsible
// sections shown above "Advance Settings".
const MAIN_SECTIONS: Section[] = [
  {
    id: 'theme',
    title: 'Theme Colour Setting',
    rows: [
      { kind: 'swatch', key: 'theme_color', label: 'A - App Colour' },
      { kind: 'note', text: 'Note: Tap a colour to preview instantly. Press Apply to keep it, Cancel to revert.' },
    ],
  },
  {
    id: 'billing-screen',
    title: '1 — Billing Screen Option',
    rows: [
      { kind: 'toggle', key: 'reverse_qty', label: '1. Calculate reverse quantity' },
      { kind: 'toggle', key: 'show_print_buttons_after_pay', label: '3. Show Print Buttons After Pay' },
      { kind: 'toggle', key: 'enable_opening_balance', label: 'A - Opening Balance' },
      { kind: 'toggle', key: 'enable_floating_keyboard', label: 'B - Floating Keyboard Button' },
      { kind: 'toggle', key: 'enable_opening_balance_amount_set', label: 'C - Enable Show Opening Amount' },
      { kind: 'note', text: 'Note: Yesterday cashInHand amount showing in Opening balance.' },
      { kind: 'toggle', key: 'disable_save_button', label: 'D - Disable Save Bill Button' },
      { kind: 'toggle', key: 'disable_print_button', label: 'E - Disable Print Bill Button' },
      { kind: 'toggle', key: 'enable_network_connection', label: 'F - Network Connection' },
      { kind: 'toggle', key: 'discount_amount_ui_option', label: 'G - Discount Amount, PER (%), DELIVERY OPTION' },
      { kind: 'toggle', key: 'wholesale', label: 'H - Retail & Wholesale' },
      { kind: 'toggle', key: 'deliveryNote', label: 'L - Delivery Challan / Vehicle' },
      { kind: 'toggle', key: 'dinein_parcel', label: 'I - Dine In & Parcel' },
      { kind: 'toggle', key: 'counter_one_two', label: 'Counter 1 & 2' },
      { kind: 'toggle', key: 'show_hold_list_button', label: 'J - Disable Hold List Button' },
      { kind: 'toggle', key: 'enable_udn', label: 'K - UDN Box' },
      { kind: 'toggle', key: 'show_bill_details_section', label: 'M - Bill Details Section (Payment Mode, Customer, Totals)' },
      { kind: 'toggle', key: 'hide_product_search_box', label: 'N - Hide Product Search Box' },
      { kind: 'toggle', key: 'allow_decimal_qty', label: 'O - Allow Decimal Qty' },
    ],
  },
  {
    id: 'customer',
    title: '2 — Customer Setting',
    rows: [
      { kind: 'toggle', key: 'customer_details', label: 'A - Customer Details' },
      { kind: 'toggle', key: 'enable_customer_advance', label: 'B - Customer Advance' },
      { kind: 'toggle', key: 'enable_customer_forcefully', label: 'C - Customer Forcefully (Bill)' },
      { kind: 'toggle', key: 'enable_vehicle_number', label: 'D - Add Vehicle Number' },
      { kind: 'toggle', key: 'enable_loyalty_points', label: 'E - Loyalty Points' },
      { kind: 'toggle', key: 'enable_transport_details', label: 'F - Transport Details' },
      { kind: 'subheader', text: 'Customer Print Option' },
      { kind: 'toggle', key: 'customer_details_show_on_header', label: '1 - Customer Details Show On Header' },
    ],
  },
  {
    id: 'menu-option',
    title: '3 — Menu Option',
    rows: [
      { kind: 'toggle', key: 'enable_payment_menu', label: 'A - CashFlow Menu' },
      { kind: 'note', text: 'Note: Add Expenses, Advance, Customer Credit, Received Amount option.' },
      { kind: 'toggle', key: 'enable_goods_received_menu', label: 'B - Goods Received Menu' },
      { kind: 'toggle', key: 'enable_stock_out_menu', label: 'C - Stock Out Menu' },
      { kind: 'toggle', key: 'barcode_menu', label: 'D - Barcode Menu' },
      { kind: 'note', text: 'Note: Connects to only TSC USB Barcode Printer.' },
      { kind: 'toggle', key: 'sales_return', label: 'E - Sales Return' },
    ],
  },
  {
    id: 'product',
    title: '4 — Product Setting',
    rows: [
      { kind: 'toggle', key: 'language', label: 'A - Regional Language' },
      { kind: 'toggle', key: 'show_images', label: 'B - Show product image' },
      { kind: 'toggle', key: 'hide_product_search_code', label: 'C - Product Search Code' },
      { kind: 'toggle', key: 'product_list_style_enable', label: 'D - Product List Style' },
      { kind: 'radio', key: 'product_list_style', label: 'List style', options: ['Standard', 'Normal', 'Small'], defaultValue: 'Normal' },
      { kind: 'toggle', key: 'do_not_show_product_list', label: 'E - Do not show product list' },
      { kind: 'toggle', key: 'hot_keys_disable', label: 'F - Disable sorting in Hot keys' },
      { kind: 'toggle', key: 'enable_three_char_search', label: 'G - Three character search' },
      { kind: 'toggle', key: 'stock_getting_minus', label: 'H - Stock Getting in Minus (Stockable Product)' },
      { kind: 'toggle', key: 'item_name_hide', label: 'I - Item Name Hide (Product Image ON)' },
      { kind: 'toggle', key: 'product_sort_by_name', label: 'K - Sort by Name' },
      { kind: 'toggle', key: 'product_total_amount', label: 'M - Disable Product Total Amt' },
      { kind: 'toggle', key: 'add_product_one_time', label: 'N - Add Product One Time' },
      { kind: 'toggle', key: 'variant', label: 'O - Variants' },
      { kind: 'toggle', key: 'product_sync', label: 'P - Product Sync' },
    ],
  },
  {
    id: 'barcode-scanner',
    title: '5 — Barcode Scanner Setting',
    rows: [
      { kind: 'toggle', key: 'search_with_only_barcode', label: 'A - Search Barcode with Quantity' },
      { kind: 'toggle', key: 'barcode_scan_direclty', label: 'B - Barcode Scan Directly (Without Enter)' },
    ],
  },
  {
    id: 'category',
    title: '6 — Category Setting',
    rows: [
      { kind: 'toggle', key: 'category_grideview_style', label: 'A - GridView Style' },
      { kind: 'radio', key: 'category_grid_count', label: 'Columns', options: ['8', '10', '12'], defaultValue: '8' },
      { kind: 'toggle', key: 'category_view_vertical', label: 'B - Category View Vertical' },
      { kind: 'toggle', key: 'category_regional', label: 'C - Regional Language Category Name' },
      { kind: 'toggle', key: 'category_discount', label: 'D - Category Wise Discount' },
    ],
  },
  {
    id: 'upi',
    title: 'UPI Setting',
    rows: [
      { kind: 'toggle', key: 'upi_button', label: 'A - UPI Print Button' },
      { kind: 'toggle', key: 'upi_save_button', label: 'B - UPI Save Button' },
      { kind: 'toggle', key: 'upi_qrcode_in_bill', label: 'C - UPI QRCODE In Bill' },
      { kind: 'toggle', key: 'upi_qrcode_in_bill_all', label: 'D - UPI QRCODE In Bill (ALL)' },
    ],
  },
  {
    id: 'keyboard',
    title: 'Keyboard Setting',
    rows: [
      { kind: 'toggle', key: 'connect_keyboard', label: 'A - Connect Keyboard' },
      { kind: 'toggle', key: 'keyboard_payment_mode_digital', label: 'B - Keyboard Enter Payment Mode Digital' },
      { kind: 'toggle', key: 'open_soft_keyboard', label: 'C - Open Soft Keyboard' },
    ],
  },
];

const DAY_END_TIMES = ['12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM'];

// The "Advance Settings" group — collapsed behind the dark-orange divider.
const ADVANCE_SECTIONS: Section[] = [
  {
    id: 'advance-general',
    title: 'General',
    rows: [
      { kind: 'toggle', key: 'manual_price', label: '7. Manual Price' },
      { kind: 'toggle', key: 'open_price', label: '9. Open Price' },
      { kind: 'toggle', key: 'bill_format_send_to_email', label: '12. Bill Send To Email' },
      { kind: 'toggle', key: 'enable_store', label: '16. Enable Store' },
      { kind: 'toggle', key: 'discount_directly_add_bill', label: '13. Discount To Directly Add Bill' },
      { kind: 'toggle', key: 'a4_delivery_note', label: '16. A4 Delivery Note' },
      { kind: 'toggle', key: 'bill_screen_loading_style', label: '25. Bill Screen Loading Style' },
    ],
  },
  {
    id: 'combo',
    title: 'Combo Setting',
    rows: [
      { kind: 'toggle', key: 'enable_combo_price_update', label: 'A. Combo Price Update' },
      { kind: 'toggle', key: 'enable_combo_add_billing', label: 'B. Combo Add In Billing' },
    ],
  },
  {
    id: 'tax',
    title: '7 — Tax Setting',
    rows: [
      { kind: 'toggle', key: 'tax_inclusive', label: 'A - Inclusive Tax' },
      { kind: 'toggle', key: 'isRoundOffAmt', label: 'B - Round Off Amount' },
      { kind: 'toggle', key: 'premises_wise_tax', label: 'C - Premises Wise Tax' },
      { kind: 'toggle', key: 'isIGST', label: 'E - IGST' },
      { kind: 'toggle', key: 'estimateMode', label: 'F - Estimate / Proforma Bills' },
      { kind: 'toggle', key: 'nonGST', label: 'G - Non-GST Outlet' },
      { kind: 'toggle', key: 'service_charge', label: 'H - Service Charge' },
    ],
  },
  {
    id: 'swiggy',
    title: '8 — Swiggy & Zomato Setting',
    rows: [
      { kind: 'toggle', key: 'online_order_system', label: 'A. Swiggy And Zomato' },
      { kind: 'toggle', key: 'disable_save_button_sz', label: 'B. Disable Save Button (Swiggy & Zomato)' },
    ],
  },
  {
    id: 'salesman',
    title: '9 — Salesman Setting',
    rows: [
      { kind: 'toggle', key: 'enable_salesman', label: 'A - Enable Salesman' },
      { kind: 'toggle', key: 'enable_salesman_forcefully', label: 'B - Salesman Forcefully' },
      { kind: 'toggle', key: 'enable_salesman_product_wise', label: 'C - Salesman Product Wise' },
    ],
  },
  {
    id: 'coupon',
    title: '8 — Coupon Setting',
    rows: [
      { kind: 'toggle', key: 'coupon_save_bill', label: 'A - Coupon Print & Bill Save' },
      { kind: 'toggle', key: 'coupon_print_bill', label: 'B - Coupon Print & Bill Print' },
      { kind: 'toggle', key: 'coupon_kot_header_on_bill', label: 'C - Coupon/KOT Header On Bill' },
      { kind: 'toggle', key: 'token_number_print', label: 'D - Token Number Print' },
      { kind: 'toggle', key: 'coupon_upi', label: 'E - Coupon UPI' },
      { kind: 'toggle', key: 'multi_coupon', label: 'F - Multi Coupon' },
      { kind: 'toggle', key: 'disable_auto_paper_cutter', label: 'G - Disable Auto Paper Cutter' },
      { kind: 'toggle', key: 'double_coupon_print', label: 'E. Double Coupon Print' },
    ],
  },
  {
    id: 'day',
    title: '10 — Day Setting',
    rows: [
      { kind: 'spinner', key: 'day_end_time', label: 'A - Day End Time', options: DAY_END_TIMES, defaultValue: '12 AM' },
      { kind: 'toggle', key: 'enable_shift', label: 'B - Enable Shift' },
      { kind: 'toggle', key: 'enable_day_night', label: 'C - Enable Day/Night' },
    ],
  },
  {
    id: 'service-point',
    title: '12 — Service Point Setting',
    rows: [
      { kind: 'toggle', key: 'multi_service_point_wise_product_print', label: 'A - Multi Service Point Wise Product Print' },
    ],
  },
  {
    id: 'settlement',
    title: '14 — Bill Settlement Setting',
    rows: [
      { kind: 'toggle', key: 'bill_settlement', label: 'A - Bill Settlement' },
      { kind: 'toggle', key: 'editing_in_settlement', label: 'B - Editing In Settlement' },
      { kind: 'toggle', key: 'self_services', label: 'C - Self Services' },
    ],
  },
  {
    id: 'table',
    title: '15 — Table Settings',
    rows: [
      { kind: 'toggle', key: 'enable_tables', label: 'A - Enable Tables' },
      { kind: 'toggle', key: 'ask_captain_details', label: 'B - Ask Captain details at bill creation' },
      { kind: 'toggle', key: 'table_forcefully', label: 'C - Table Forcefully' },
      { kind: 'toggle', key: 'checkin_checkout', label: 'E - CheckIn/CheckOut' },
      { kind: 'toggle', key: 'table_coupon', label: 'F - Table Coupon' },
      { kind: 'toggle', key: 'show_premises_first', label: 'G - Show Premises First In Billing Screen' },
      { kind: 'toggle', key: 'table_list_style', label: 'H - Table List Style' },
      { kind: 'radio', key: 'table_list_style_choice', label: 'Style', options: ['Standard', 'Regular'], defaultValue: 'Standard' },
      { kind: 'toggle', key: 'abcd_table_split', label: 'I - ABCD Table Split' },
      { kind: 'toggle', key: 'table_book_without_kot', label: 'J - Table Book Without KOT' },
      { kind: 'toggle', key: 'enter_token_number', label: 'J - Enter Token Number' },
      { kind: 'toggle', key: 'table_extra_actions', label: 'K - Table Extra Actions Menu' },
      { kind: 'subheader', text: 'Table Extra Actions (each needs K on)' },
      { kind: 'toggle', key: 'table_action_swap', label: 'K1 - Swap Table' },
      { kind: 'toggle', key: 'table_action_transfer', label: 'K2 - Transfer to Free Table' },
      { kind: 'toggle', key: 'table_action_merge', label: 'K3 - Merge into Table' },
      { kind: 'toggle', key: 'table_action_move_items', label: 'K4 - Move Items to Table' },
      { kind: 'toggle', key: 'table_action_bill_merge', label: 'K5 - Bill Merge (pay together)' },
      { kind: 'toggle', key: 'table_action_split_bill', label: 'K6 - Split Bill (pay part)' },
      { kind: 'toggle', key: 'table_action_equal_split', label: 'K7 - Equal Split (N bills)' },
      { kind: 'toggle', key: 'table_action_print_bill', label: 'K8 - Print Bill (pre-bill)' },
      { kind: 'toggle', key: 'table_action_void', label: 'K9 - Void Table' },
    ],
  },
  {
    id: 'captain',
    title: '17 — Captain App Setting',
    rows: [
      { kind: 'toggle', key: 'captain_app', label: 'A. Captain App' },
      { kind: 'toggle', key: 'captain_disable_print_button', label: 'B. Disable Print Button' },
      { kind: 'toggle', key: 'captain_wise_premise', label: 'C. Captain Wise Premise' },
      { kind: 'toggle', key: 'captain_table_actions', label: 'D. Table Actions Menu on Captain App' },
      { kind: 'subheader', text: 'Captain Table Actions (each needs D on)' },
      { kind: 'toggle', key: 'captain_action_swap', label: 'D1 - Swap Table' },
      { kind: 'toggle', key: 'captain_action_transfer', label: 'D2 - Transfer to Free Table' },
      { kind: 'toggle', key: 'captain_action_merge', label: 'D3 - Merge into Table' },
      { kind: 'toggle', key: 'captain_action_move_items', label: 'D4 - Move Items to Table' },
      { kind: 'toggle', key: 'captain_action_bill_merge', label: 'D5 - Bill Merge (pay together)' },
      { kind: 'toggle', key: 'captain_action_split_bill', label: 'D6 - Split Bill (pay part)' },
      { kind: 'toggle', key: 'captain_action_equal_split', label: 'D7 - Equal Split (N bills)' },
      { kind: 'toggle', key: 'captain_action_print_bill', label: 'D8 - Print Bill (pre-bill)' },
      { kind: 'toggle', key: 'captain_action_void', label: 'D9 - Void Table' },
    ],
  },
  {
    id: 'kot',
    title: '16 — KOT Setting',
    rows: [
      { kind: 'toggle', key: 'kot_reprint_button', label: '1 - KOT Re-Print Button' },
      { kind: 'toggle', key: 'kot_remark', label: '2 - KOT Remark' },
      { kind: 'toggle', key: 'disable_save_kot_button', label: '3 - Disable Save KOT Button' },
      { kind: 'toggle', key: 'disable_print_kot_button', label: '4 - Disable Print KOT Button' },
      { kind: 'note', text: 'KOT always prints to the USB printer (per-station routing under Service Points).' },
      { kind: 'toggle', key: 'no_bill_without_kot', label: "6 - Don't Save/Print Bill Without KOT" },
      { kind: 'subheader', text: 'KOT Print Option' },
      { kind: 'toggle', key: 'kot_qty_single_letter', label: '1 - Qty Show Single Letter' },
      { kind: 'toggle', key: 'kot_qty_show_weight', label: '2 - Qty Show in Weight' },
      { kind: 'toggle', key: 'kot_billno_sno', label: '3 - Bill No & S.NO in Kot|Coupon Print' },
      { kind: 'toggle', key: 'kot_print_amount_column', label: '4 - KOT Print Amount Column' },
      { kind: 'toggle', key: 'kot_space_less', label: '5 - Space Less' },
      { kind: 'toggle', key: 'kot_add_space', label: '5 - Add Space' },
      { kind: 'toggle', key: 'kot_add_space_top', label: '6 - Add Space (Top)' },
      { kind: 'toggle', key: 'kot_large_font', label: '7 - Large Font KOT' },
      { kind: 'toggle', key: 'kot_disable_new_header', label: '8 - Disable NEW KOT (Header)' },
      { kind: 'toggle', key: 'kot_show_product_total_qty', label: '9 - Show Product Total Qty' },
      { kind: 'toggle', key: 'new_coupon_format_qty_price', label: '10 - New Coupon Format (QTY & PRICE)' },
    ],
  },
];

// Resolve a toggle's effective on/off honoring the documented defaults, so
// menus/features that ship enabled render as ON before the user touches them.
const resolveOn = (settings: AppSettingsType, key: string): boolean => {
  if (key in settings) return settings[key] === true;
  return APP_SETTING_DEFAULTS[key] === true;
};
const resolveChoice = (settings: AppSettingsType, key: string, fallback: string): string => {
  const v = key in settings ? settings[key] : APP_SETTING_DEFAULTS[key];
  return typeof v === 'string' ? v : fallback;
};

const AppSettingsPage: React.FC = () => {
  const { appSettings, updateAppSettings } = usePos();
  const [settings, setSettings] = useState<AppSettingsType>(appSettings);
  // Section ids that are collapsed (default: everything expanded for discoverability).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [advanceOpen, setAdvanceOpen] = useState(false);

  const dirty = useMemo(() => JSON.stringify(settings) !== JSON.stringify(appSettings), [settings, appSettings]);

  const toggle = (key: string) =>
    setSettings((prev) => ({ ...prev, [key]: !resolveOn(prev, key) }));
  const choose = (key: string, value: string) =>
    setSettings((prev) => ({ ...prev, [key]: value }));
  const collapse = (id: string) =>
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  const apply = () => updateAppSettings(settings);
  const cancel = () => setSettings(appSettings);

  // Live colour preview: tint the UI with the pending choice while on this
  // screen; the PosContext effect re-applies the saved theme after Apply,
  // and Cancel resets `settings` so this effect reverts the preview too.
  const themeChoice = resolveChoice(settings, 'theme_color', DEFAULT_THEME_ID);
  const savedTheme = resolveChoice(appSettings, 'theme_color', DEFAULT_THEME_ID);
  useEffect(() => {
    applyTheme(themeChoice);
    return () => applyTheme(savedTheme);
  }, [themeChoice, savedTheme]);

  const renderRow = (row: Row, i: number) => {
    switch (row.kind) {
      case 'note':
        return (
          <div key={i} className="as-note">
            {row.text}
          </div>
        );
      case 'subheader':
        return (
          <div key={i} className="as-subheader">
            {row.text}
          </div>
        );
      case 'swatch': {
        const value = resolveChoice(settings, row.key, DEFAULT_THEME_ID);
        return (
          <div key={i} className="as-swatch-row">
            <span className="as-label">{row.label}</span>
            <div className="as-swatches">
              {THEME_PRESETS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`as-swatch ${value === t.id ? 'active' : ''}`}
                  style={{ background: t.primary }}
                  title={t.label}
                  aria-label={t.label}
                  aria-pressed={value === t.id}
                  onClick={() => choose(row.key, t.id)}
                >
                  {value === t.id ? '✓' : ''}
                </button>
              ))}
            </div>
          </div>
        );
      }
      case 'toggle': {
        const on = resolveOn(settings, row.key);
        return (
          <div key={i} className="as-row" onClick={() => toggle(row.key)}>
            <span className="as-label">{row.label}</span>
            <button
              type="button"
              className={`as-switch ${on ? 'on' : ''}`}
              role="switch"
              aria-checked={on}
              aria-label={row.label}
              onClick={(e) => {
                e.stopPropagation();
                toggle(row.key);
              }}
            >
              <span className="as-knob" />
            </button>
          </div>
        );
      }
      case 'radio': {
        const value = resolveChoice(settings, row.key, row.defaultValue);
        return (
          <div key={i} className="as-radio-row">
            <span className="as-label">{row.label}</span>
            <div className="as-radio-group">
              {row.options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`as-chip ${value === opt ? 'active' : ''}`}
                  onClick={() => choose(row.key, opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        );
      }
      case 'spinner': {
        const value = resolveChoice(settings, row.key, row.defaultValue);
        return (
          <div key={i} className="as-radio-row">
            <span className="as-label">{row.label}</span>
            <select className="as-select" value={value} onChange={(e) => choose(row.key, e.target.value)}>
              {row.options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        );
      }
    }
  };

  const renderSection = (section: Section) => {
    const isCollapsed = !!collapsed[section.id];
    return (
      <div key={section.id} className="as-card">
        <button type="button" className="as-card-header" onClick={() => collapse(section.id)}>
          <span>{section.title}</span>
          <span className={`as-chevron ${isCollapsed ? '' : 'open'}`}>▾</span>
        </button>
        {!isCollapsed && <div className="as-card-body">{section.rows.map(renderRow)}</div>}
      </div>
    );
  };

  return (
    <div className="as-page">
      <div className="as-topbar">
        <h1>App Setting</h1>
        {dirty && (
          <div className="as-apply">
            <span>Apply new changes?</span>
            <button className="as-btn-cancel" onClick={cancel}>
              Cancel
            </button>
            <button className="as-btn-apply" onClick={apply}>
              Apply
            </button>
          </div>
        )}
      </div>

      <div className="as-grid">{MAIN_SECTIONS.map(renderSection)}</div>

      <button
        type="button"
        className="as-advance-header"
        onClick={() => setAdvanceOpen((v) => !v)}
      >
        <span>Advance Settings</span>
        <span className={`as-chevron ${advanceOpen ? 'open' : ''}`}>▾</span>
      </button>
      {advanceOpen && <div className="as-grid">{ADVANCE_SECTIONS.map(renderSection)}</div>}
    </div>
  );
};

export default AppSettingsPage;
